import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Music2, Mic, Gauge, Image, Zap, Disc3, Headphones, Crown, Check, ChevronDown, Sparkles } from 'lucide-react';

// Background images - studios & producers
const IMG_HERO = 'https://images.unsplash.com/photo-1760780567530-389d8a3fba75?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920';
const IMG_CONSOLE = 'https://images.unsplash.com/photo-1767474833853-709a497fe4f1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400';
const IMG_PRODUCERS = 'https://images.unsplash.com/photo-1761652556225-1425a7c36247?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400';
const IMG_SINGER = 'https://images.unsplash.com/photo-1543510616-a4025b9c1434?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400';
const IMG_DARK_STUDIO = 'https://images.unsplash.com/photo-1610716632424-4d45990bcd48?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400';
const IMG_MIC = 'https://images.unsplash.com/photo-1579870946215-8284f1a47c9a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.5, ease: 'easeOut' } })
};

const GENRE_TAGS = [
  'Sertanejo', 'Forró', 'Funk Carioca', 'Reggaeton', 'Salsa', 'Axé',
  'Samba', 'Pagode', 'Afrobeats', 'Rock', 'Pop', 'K-Pop',
  'Trap', 'MPB', 'Bossa Nova', 'Drill', 'Synthwave', 'R&B'
];

const TESTIMONIALS = [
  { name: "Lucas M.", role: "Produtor Musical", quote: "O MUNOMUSIC mudou meu workflow. Gero prompts profissionais em segundos e o resultado no Suno é incrível!", initials: "LM" },
  { name: "Ana C.", role: "Cantora & Compositora", quote: "Adoro o modo Estilo Similar! Consigo capturar a vibe de qualquer artista sem problemas de copyright.", initials: "AC" },
  { name: "Rafael S.", role: "DJ & Beatmaker", quote: "160+ subgêneros e a geração de capas com IA? Isso é o futuro da produção musical.", initials: "RS" }
];

const STEPS = [
  { num: "01", title: "Escolha seu Estilo", desc: "Selecione entre 29 categorias e 160+ subgêneros de todo o mundo.", icon: <Music2 size={22} /> },
  { num: "02", title: "Ajuste Vocal e Andamento", desc: "Configure tipo de voz, velocidade BPM e até 2 vibes.", icon: <Mic size={22} /> },
  { num: "03", title: "MUNOMUSIC Gera Tudo", desc: "Prompt profissional + letra + capa gerados por IA em segundos.", icon: <Zap size={22} /> },
  { num: "04", title: "Cole no Suno, Udio & Mais", desc: "Copie o prompt, cole no seu app favorito e ouça a mágica.", icon: <Headphones size={22} /> }
];

export default function LandingPage({ onStart }) {
  const ritmosRef = useRef(null);
  const comoRef = useRef(null);
  const planosRef = useRef(null);

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth' });

  const handleUpgradePro = () => {
    window.open('https://buy.stripe.com/9B67sMcPj2xdfK1fKu0Jq03', '_blank');
  };

  return (
    <div style={{ background: 'linear-gradient(180deg, #122952 0%, #163562 30%, #133058 60%, #102848 100%)', color: '#F8FAFC', fontFamily: "'Outfit', sans-serif", minHeight: '100vh' }}>
      
      {/* NAV */}
      <nav className="fixed top-0 w-full z-50 py-4 px-6 md:px-12 lg:px-20 flex items-center justify-between" style={{ background: 'rgba(18,41,82,0.8)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,240,255,0.12)' }} data-testid="landing-nav">
        <span style={{ fontFamily: "'Unbounded', sans-serif", color: '#00F0FF' }} className="text-lg font-black tracking-tight">MUNOMUSIC</span>
        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => scrollTo(ritmosRef)} className="text-sm font-medium hover:text-cyan-300 transition-colors" style={{ color: '#94A3B8' }}>Ritmos</button>
          <button onClick={() => scrollTo(comoRef)} className="text-sm font-medium hover:text-cyan-300 transition-colors" style={{ color: '#94A3B8' }}>Como Funciona</button>
          <button onClick={() => scrollTo(planosRef)} className="text-sm font-medium hover:text-cyan-300 transition-colors" style={{ color: '#94A3B8' }}>Planos</button>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onStart} className="text-sm font-semibold px-4 py-2 rounded-xl transition-all hover:text-white" style={{ color: '#00F0FF' }}>Entrar</button>
          <button onClick={onStart} className="text-sm font-bold px-5 py-2.5 rounded-xl transition-all" style={{ background: '#00F0FF', color: '#0A1628', boxShadow: '0 0 20px rgba(0,240,255,0.3)' }} data-testid="nav-start-button">
            Começar Grátis
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-32 pb-16 md:pt-44 md:pb-28 px-6 text-center overflow-hidden" data-testid="hero-section">
        {/* Background image - studio */}
        <img src={IMG_HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" style={{ objectPosition: 'center 40%' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(18,41,82,0.2) 0%, rgba(18,41,82,0.35) 40%, rgba(18,41,82,0.7) 80%, #122952 100%)' }} />
        
        {/* Glow effects */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-15 blur-[120px]" style={{ background: 'radial-gradient(circle, #00F0FF, transparent)' }} />

        <div className="relative z-10 max-w-4xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0} className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8" style={{ background: 'rgba(0,240,255,0.1)', border: '1px solid rgba(0,240,255,0.25)' }}>
            <Sparkles size={14} style={{ color: '#00F0FF' }} />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#00F0FF' }}>Para Suno, Udio, Loudly & Mais</span>
          </motion.div>

          <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={1} style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight mb-6 leading-[1.1]">
            <span className="text-white">Sua Música</span>
            <br />
            <span style={{ background: 'linear-gradient(135deg, #00F0FF, #39FF14)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Começa Aqui</span>
          </motion.h1>

          <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={2} className="text-base md:text-lg mb-10 max-w-2xl mx-auto leading-relaxed" style={{ color: '#94A3B8' }}>
            Gere prompts perfeitos para apps de produção musical, explore 160+ ritmos do Brasil e do mundo, escreva letras completas e crie capas de álbum — em segundos.
          </motion.p>

          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <button onClick={onStart} className="px-8 py-4 rounded-xl font-bold text-base transition-all hover:scale-105" style={{ background: '#00F0FF', color: '#0A1628', boxShadow: '0 0 30px rgba(0,240,255,0.4)' }} data-testid="hero-start-button">
              Começar Grátis
            </button>
            <button onClick={() => scrollTo(comoRef)} className="flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-base transition-all hover:bg-white/5" style={{ border: '2px solid rgba(255,255,255,0.15)', color: '#94A3B8' }}>
              Ver Demo <ChevronDown size={16} />
            </button>
          </motion.div>

          {/* Genre Tags */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4} className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto" ref={ritmosRef} data-testid="genre-tags">
            {GENRE_TAGS.map((tag, i) => (
              <span key={tag} className="px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-default hover:scale-105" style={{ background: 'rgba(0,240,255,0.08)', border: '1px solid rgba(0,240,255,0.15)', color: '#67E8F9' }}>
                {tag}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 relative overflow-hidden" data-testid="features-section">
        {/* Subtle studio background across the entire section */}
        <img src={IMG_CONSOLE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #122952 0%, rgba(18,41,82,0.45) 20%, rgba(18,41,82,0.45) 80%, #122952 100%)' }} />
        
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14 relative z-10">
          <p className="uppercase tracking-[0.2em] text-xs font-bold mb-3" style={{ color: '#39FF14' }}>Funcionalidades</p>
          <h2 style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-2xl md:text-4xl font-bold text-white tracking-tight">Tudo para o Prompt Perfeito</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 max-w-6xl mx-auto relative z-10">
          {/* Big card */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} className="col-span-12 md:col-span-7 rounded-2xl p-8 relative overflow-hidden" style={{ border: '1px solid rgba(0,240,255,0.15)' }}>
            <img src={IMG_CONSOLE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(18,41,82,0.55), rgba(18,41,82,0.4))' }} />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <Disc3 size={20} style={{ color: '#39FF14' }} />
                <span className="uppercase tracking-[0.15em] text-xs font-bold" style={{ color: '#39FF14' }}>Biblioteca Global</span>
              </div>
              <h3 style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-xl md:text-2xl font-bold text-white mb-3">160+ Subgêneros de 29 Categorias</h3>
              <p style={{ color: '#94A3B8' }} className="text-sm leading-relaxed">Sertanejo, Forró, Reggae, Synthwave, K-Pop, Afrobeat, MPB, Bossa Nova e muito mais. Ritmos regionais do Brasil e do mundo inteiro.</p>
            </div>
          </motion.div>

          {/* Cover Art */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="col-span-12 md:col-span-5 rounded-2xl p-8 relative overflow-hidden" style={{ border: '1px solid rgba(0,240,255,0.12)' }}>
            <img src={IMG_DARK_STUDIO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(18,41,82,0.5), rgba(18,41,82,0.45))' }} />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <Image size={20} style={{ color: '#00F0FF' }} />
                <span className="uppercase tracking-[0.15em] text-xs font-bold" style={{ color: '#00F0FF' }}>IA Generativa</span>
              </div>
              <h3 style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-lg font-bold text-white mb-2">Capas com IA</h3>
              <p style={{ color: '#94A3B8' }} className="text-sm">Gere artes de capa profissionais que combinam com a vibe da sua música. Baixe em alta qualidade.</p>
            </div>
          </motion.div>

          {/* Vocal & Speed */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} className="col-span-12 md:col-span-6 rounded-2xl p-8 relative overflow-hidden" style={{ border: '1px solid rgba(57,255,20,0.1)' }}>
            <img src={IMG_MIC} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0" style={{ background: 'rgba(18,41,82,0.5)' }} />
            <div className="relative z-10 flex items-center gap-3 mb-4">
              <Mic size={20} style={{ color: '#00F0FF' }} />
              <Gauge size={20} style={{ color: '#39FF14' }} />
            </div>
            <h3 style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-lg font-bold text-white mb-2 relative z-10">Vocal & Velocidade</h3>
            <p style={{ color: '#94A3B8' }} className="text-sm relative z-10">8 tipos de vocal + 4 faixas de BPM com preview de metrônomo ao vivo.</p>
          </motion.div>

          {/* Modes */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={3} className="col-span-12 md:col-span-6 rounded-2xl p-8 relative overflow-hidden" style={{ border: '1px solid rgba(0,240,255,0.1)' }}>
            <img src={IMG_SINGER} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0" style={{ background: 'rgba(18,41,82,0.5)' }} />
            <div className="relative z-10 flex items-center gap-3 mb-4">
              <Sparkles size={20} style={{ color: '#00F0FF' }} />
              <Music2 size={20} style={{ color: '#39FF14' }} />
            </div>
            <h3 style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-lg font-bold text-white mb-2 relative z-10">Estilo Similar</h3>
            <p style={{ color: '#94A3B8' }} className="text-sm relative z-10">Analisa qualquer artista e gera prompts no mesmo estilo — sem revelar o nome. Salve seus presets favoritos.</p>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section ref={comoRef} className="py-20 md:py-28 px-6 md:px-12 lg:px-20 relative overflow-hidden" data-testid="how-it-works-section">
        <img src={IMG_PRODUCERS} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #122952 0%, rgba(18,41,82,0.35) 30%, rgba(18,41,82,0.35) 70%, #122952 100%)' }} />
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14 relative z-10">
          <p className="uppercase tracking-[0.2em] text-xs font-bold mb-3" style={{ color: '#00F0FF' }}>Como Funciona</p>
          <h2 style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-2xl md:text-4xl font-bold text-white tracking-tight">Do Prompt à Playlist em Segundos</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto relative z-10">
          {STEPS.map((step, i) => (
            <motion.div key={step.num} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} className="text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(0,240,255,0.1)', border: '1px solid rgba(0,240,255,0.2)', color: '#00F0FF', boxShadow: '0 0 25px rgba(0,240,255,0.15)' }}>
                {step.icon}
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: '#39FF14' }}>{step.num}</span>
              <h3 style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-sm font-bold text-white mt-2 mb-2">{step.title}</h3>
              <p style={{ color: '#94A3B8' }} className="text-xs leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Demo Video */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-3xl mx-auto mt-16 relative z-10" data-testid="demo-video">
          <div className="rounded-2xl overflow-hidden border-2 border-cyan-400/30 relative group cursor-pointer" style={{ boxShadow: '0 0 50px rgba(0,240,255,0.2)' }}
            onClick={(e) => {
              const video = e.currentTarget.querySelector('video');
              if (video) {
                if (video.paused) { video.play(); e.currentTarget.querySelector('.play-overlay')?.style.setProperty('display', 'none'); }
                else { video.pause(); e.currentTarget.querySelector('.play-overlay')?.style.setProperty('display', 'flex'); }
              }
            }}
          >
            <video 
              controls 
              playsInline
              preload="auto"
              className="w-full"
              style={{ aspectRatio: '16/9', background: '#0a1628' }}
              onPlay={(e) => e.target.closest('.group')?.querySelector('.play-overlay')?.style.setProperty('display', 'none')}
              onPause={(e) => e.target.closest('.group')?.querySelector('.play-overlay')?.style.setProperty('display', 'flex')}
            >
              <source src="/demo-video.mp4" type="video/mp4" />
            </video>
            {/* Big Play Overlay */}
            <div className="play-overlay absolute inset-0 flex items-center justify-center bg-black/40" style={{ pointerEvents: 'none' }}>
              <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00F0FF, #39FF14)', boxShadow: '0 0 40px rgba(0,240,255,0.5)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="#0a1628"><polygon points="9.5,7.5 16.5,12 9.5,16.5" /></svg>
              </div>
            </div>
          </div>
          <p className="text-center mt-4 text-xs font-bold uppercase tracking-widest" style={{ color: '#39FF14' }}>Veja o MUNOMUSIC em ação</p>
        </motion.div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 relative overflow-hidden" data-testid="testimonials-section">
        <img src={IMG_DARK_STUDIO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #122952 0%, rgba(18,41,82,0.45) 30%, rgba(18,41,82,0.45) 70%, #122952 100%)' }} />
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14 relative z-10">
          <p className="uppercase tracking-[0.2em] text-xs font-bold mb-3" style={{ color: '#00F0FF' }}>Depoimentos</p>
          <h2 style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-2xl md:text-4xl font-bold text-white tracking-tight">Aprovado por Produtores</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto relative z-10">
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={t.name} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} className="rounded-2xl p-7" style={{ background: 'rgba(13,31,60,0.5)', border: '1px solid rgba(0,240,255,0.1)' }} data-testid={`testimonial-${i}`}>
              <p style={{ color: '#CBD5E1' }} className="text-sm mb-6 italic leading-relaxed">"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'rgba(0,240,255,0.12)', color: '#00F0FF', border: '1px solid rgba(0,240,255,0.25)' }}>
                  {t.initials}
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{t.name}</p>
                  <p className="text-xs" style={{ color: '#94A3B8' }}>{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section ref={planosRef} className="py-20 md:py-28 px-6 md:px-12 lg:px-20 relative overflow-hidden" data-testid="pricing-section">
        <img src={IMG_MIC} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #122952 0%, rgba(18,41,82,0.48) 30%, rgba(18,41,82,0.48) 70%, #122952 100%)' }} />
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14 relative z-10">
          <p className="uppercase tracking-[0.2em] text-xs font-bold mb-3" style={{ color: '#39FF14' }}>Planos</p>
          <h2 style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-2xl md:text-4xl font-bold text-white tracking-tight">Preços para cada Criador</h2>
          <p className="mt-3 text-sm" style={{ color: '#94A3B8' }}>Sem surpresas. Cancele quando quiser.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto relative z-10">
          {/* Free */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0} className="rounded-2xl p-8" style={{ background: 'rgba(13,31,60,0.5)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="plan-free">
            <p className="uppercase tracking-[0.15em] text-xs font-bold mb-3" style={{ color: '#94A3B8' }}>Gratuito</p>
            <div className="mb-5">
              <span style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-4xl font-black text-white">R$ 0</span>
              <span className="text-sm ml-1" style={{ color: '#94A3B8' }}>/mês</span>
            </div>
            <ul className="space-y-3 mb-8">
              {['2 prompts por dia', 'Todos os ritmos', 'Gerador de estilo básico'].map(f => (
                <li key={f} className="flex items-center gap-2 text-sm" style={{ color: '#94A3B8' }}><Check size={14} style={{ color: '#39FF14' }} /> {f}</li>
              ))}
            </ul>
            <button onClick={onStart} className="w-full py-3 rounded-xl font-bold text-sm transition-all hover:bg-white/5" style={{ border: '1px solid rgba(255,255,255,0.12)', color: '#94A3B8' }}>
              Começar Grátis
            </button>
          </motion.div>

          {/* Pro */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="rounded-2xl p-8 relative" style={{ background: 'linear-gradient(135deg, rgba(0,240,255,0.06), rgba(57,255,20,0.04))', border: '2px solid rgba(0,240,255,0.3)' }} data-testid="plan-pro">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] font-black uppercase" style={{ background: '#00F0FF', color: '#0A1628' }}>
              Popular
            </div>
            <div className="flex items-center gap-2 mb-3">
              <Crown size={16} style={{ color: '#00F0FF' }} />
              <p className="uppercase tracking-[0.15em] text-xs font-bold" style={{ color: '#00F0FF' }}>Pro</p>
            </div>
            <div className="mb-5">
              <span style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-4xl font-black text-white">R$ 29<span className="text-lg">,90</span></span>
              <span className="text-sm ml-1" style={{ color: '#94A3B8' }}>/mês</span>
            </div>
            <ul className="space-y-3 mb-8">
              {['Prompts ilimitados', 'Todos os 160+ ritmos', 'Capas de álbum com IA', 'Letras completas', 'Presets personalizados', 'Suporte prioritário'].map(f => (
                <li key={f} className="flex items-center gap-2 text-sm text-white"><Check size={14} style={{ color: '#00F0FF' }} /> {f}</li>
              ))}
            </ul>
            <button onClick={handleUpgradePro} className="w-full py-3.5 rounded-xl font-bold text-sm transition-all hover:scale-[1.02]" style={{ background: '#00F0FF', color: '#0A1628', boxShadow: '0 0 25px rgba(0,240,255,0.3)' }} data-testid="plan-pro-button">
              Assinar Pro
            </button>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 px-6 text-center relative overflow-hidden" data-testid="footer-cta-section">
        <img src={IMG_SINGER} alt="" className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #122952 0%, rgba(18,41,82,0.25) 30%, rgba(18,41,82,0.25) 70%, #122952 100%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(0,240,255,0.1), transparent 60%)' }} />
        <div className="relative z-10">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ fontFamily: "'Unbounded', sans-serif" }} className="text-2xl md:text-4xl font-black text-white tracking-tight mb-8">
            Pronto para dominar as paradas?
          </motion.h2>
          <motion.button variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} onClick={onStart} className="px-10 py-4 rounded-xl font-bold text-base transition-all hover:scale-105" style={{ background: '#00F0FF', color: '#0A1628', boxShadow: '0 0 30px rgba(0,240,255,0.4)' }} data-testid="footer-start-button">
            Começar Grátis
          </motion.button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-6 px-6 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <p className="text-xs" style={{ color: '#64748B' }}>© 2026 MUNOMUSIC - Prompts para Suno AI, Udio & mais.</p>
      </footer>
    </div>
  );
}
